// ABOUTME: Fetch wrapper for API calls with JWT auth and automatic token refresh
// ABOUTME: All React Query hooks use this client instead of raw fetch

import { jwtHeaders, clearTokens, setJwt, getSessionToken } from './auth-token'

const BASE_URL = import.meta.env.VITE_API_URL || ''

export class ApiError extends Error {
  constructor(
    public status: number,
    public body: unknown,
  ) {
    super(`API error ${status}`)
  }
}

let refreshPromise: Promise<string | null> | null = null

export async function refreshToken(): Promise<string | null> {
  if (!refreshPromise) {
    refreshPromise = doRefresh().finally(() => { refreshPromise = null })
  }
  return refreshPromise
}

async function doRefresh(): Promise<string | null> {
  const headers: Record<string, string> = {}
  const bearer = getSessionToken()
  if (bearer) {
    headers.Authorization = `Bearer ${bearer}`
  }
  const res = await fetch(`${BASE_URL}/api/auth/token`, {
    credentials: 'include',
    headers,
  })

  if (!res.ok) return null

  const data = (await res.json()) as { token?: string }
  if (data.token) {
    setJwt(data.token)
    return data.token
  }
  return null
}

async function authPost<T>(path: string, data?: unknown): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  const bearer = getSessionToken()
  if (bearer) {
    headers.Authorization = `Bearer ${bearer}`
  }

  const res = await fetch(`${BASE_URL}${path}`, {
    method: 'POST',
    credentials: 'include',
    headers,
    body: data ? JSON.stringify(data) : undefined,
  })

  if (!res.ok) {
    const body = await res.json().catch(() => null)
    throw new ApiError(res.status, body)
  }

  if (res.status === 204) return undefined as T
  return res.json() as Promise<T>
}

async function upload<T>(path: string, formData: FormData): Promise<T> {
  const res = await fetch(`${BASE_URL}${path}`, {
    method: 'POST',
    headers: { ...jwtHeaders() },
    body: formData,
  })

  if (res.status === 401) {
    const newToken = await refreshToken()
    if (!newToken) {
      clearTokens()
    }
    throw new ApiError(401, { error: 'Unauthorized' })
  }

  if (!res.ok) {
    const body = await res.json().catch(() => null)
    throw new ApiError(res.status, body)
  }

  if (res.status === 204) return undefined as T
  return res.json() as Promise<T>
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const res = await fetch(`${BASE_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...jwtHeaders(),
      ...options.headers,
    },
  })

  if (res.status === 401) {
    const newToken = await refreshToken()
    if (newToken) {
      const retry = await fetch(`${BASE_URL}${path}`, {
        ...options,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${newToken}`,
          ...options.headers,
        },
      })
      if (!retry.ok) {
        const body = await retry.json().catch(() => null)
        throw new ApiError(retry.status, body)
      }
      if (retry.status === 204) return undefined as T
      return retry.json() as Promise<T>
    }
    clearTokens()
    throw new ApiError(401, { error: 'Unauthorized' })
  }

  if (!res.ok) {
    const body = await res.json().catch(() => null)
    throw new ApiError(res.status, body)
  }

  if (res.status === 204) return undefined as T

  return res.json() as Promise<T>
}

export const api = {
  get: <T>(path: string) => request<T>(path),

  post: <T>(path: string, data?: unknown) =>
    request<T>(path, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    }),

  put: <T>(path: string, data?: unknown) =>
    request<T>(path, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    }),

  delete: <T>(path: string) =>
    request<T>(path, { method: 'DELETE' }),

  authPost: <T>(path: string, data?: unknown) => authPost<T>(path, data),

  upload: <T>(path: string, formData: FormData) => upload<T>(path, formData),
}
